import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Aviso, Botao, EstadoCarregando, Modal } from '../componentes/ui';
import { useEu } from '../hooks/useEu';
import { useEmpresaCorrente, useEscolherEmpresa, useIdentidade } from '../hooks/useSessao';
import { mensagemDoErro } from '../servicos/erros';
import { renovarSessao } from '../servicos/http';
import {
  EscolhaDeEmpresa,
  itensParaEscolha,
  type ItemDeEscolha,
} from '../paginas/SelecionarEmpresa';

/**
 * "Trocar empresa": a lista das empresas da conta, numa janela sobre a tela.
 *
 * ## Por que não era o botão de antes
 *
 * O botão só limpava a empresa corrente e contava que a rota protegida mandasse
 * para o seletor. Só que o seletor é para quem **precisa** escolher — mais de
 * um vínculo. Com um vínculo só, nada redirecionava: o nome da empresa sumia da
 * barra, o botão sumia junto e a tela ficava igual. Aqui a escolha acontece na
 * própria janela, e a empresa corrente só muda quando outra é escolhida.
 *
 * ## A sessão é renovada ao abrir
 *
 * Os vínculos que valem são os do token (o servidor confere a empresa pedida
 * contra ele). Quem acabou de ser incluído como colaborador de outra empresa só
 * ganharia o vínculo na próxima renovação, minutos depois — e a empresa nova
 * não apareceria justamente quando a pessoa foi procurá-la. Renovar ao abrir
 * traz os vínculos de agora; os nomes vêm de `/auth/eu`, recarregado junto.
 *
 * ## Trocar leva ao painel
 *
 * A tela aberta era da empresa anterior. Ficar nela mostraria, por um instante,
 * números de uma empresa sob o nome de outra. O painel é o começo de qualquer
 * empresa, e todas as consultas são marcadas como velhas na troca.
 */
export function TrocarEmpresa(props: {
  readonly aberto: boolean;
  readonly aoFechar: () => void;
}): JSX.Element {
  const identidade = useIdentidade();
  const atual = useEmpresaCorrente();
  const escolherEmpresa = useEscolherEmpresa();
  const eu = useEu();
  const queryClient = useQueryClient();
  const navegar = useNavigate();
  const [atualizando, setAtualizando] = useState(false);
  const [recusada, setRecusada] = useState(false);

  const { aberto } = props;
  const recarregarEu = eu.refetch;
  useEffect(() => {
    if (!aberto) return;
    let cancelado = false;
    setRecusada(false);
    setAtualizando(true);
    void (async () => {
      try {
        await renovarSessao();
      } catch {
        // Sem renovar, a lista é a da sessão atual — ainda serve para trocar
        // entre as empresas que ela já conhece.
      }
      await recarregarEu();
      if (!cancelado) setAtualizando(false);
    })();
    return () => {
      cancelado = true;
    };
  }, [aberto, recarregarEu]);

  const itens: readonly ItemDeEscolha[] = itensParaEscolha(
    eu.data,
    identidade?.vinculos ?? [],
    identidade?.isAdmin === true,
  );

  const escolher = (businessId: string): boolean => {
    if (businessId === atual) return false;
    const trocou = escolherEmpresa(businessId);
    if (!trocou) {
      // Vínculo que o `/auth/eu` conhece e o token ainda não: sem renovar, a
      // escolha seria recusada pelo servidor em toda requisição.
      setRecusada(true);
      return false;
    }
    void queryClient.invalidateQueries();
    props.aoFechar();
    navegar('/painel');
    return true;
  };

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={props.aoFechar}
      titulo="Trocar empresa"
      descricao="As empresas em que sua conta é gestora ou colaboradora. Tudo o que você vê a seguir é da empresa escolhida."
      rodape={
        <Botao variante="secundario" onClick={props.aoFechar}>
          Fechar
        </Botao>
      }
    >
      <div className="flex flex-col gap-4">
        {atualizando && eu.data === undefined ? (
          <EstadoCarregando contexto="as empresas da sua conta" />
        ) : (
          <EscolhaDeEmpresa
            // Nova montagem a cada abertura: o seletor volta para a empresa em
            // uso, em vez de guardar a escolha abandonada da última vez.
            key={`${String(props.aberto)}-${atual ?? ''}`}
            rotuloDoBotao="Trocar"
            itens={itens}
            atual={atual}
            erroDeNomes={eu.isError ? mensagemDoErro(eu.error) : null}
            aoEscolher={escolher}
          />
        )}

        {recusada && (
          <Aviso tom="atencao" titulo="Este vínculo ainda não chegou à sua sessão">
            A empresa aparece no seu cadastro, mas a sessão aberta ainda não a reconhece. Saia e
            entre de novo para usá-la.
          </Aviso>
        )}

        {!atualizando && itens.length === 1 && (
          <Aviso tom="informacao" titulo="Sua conta tem uma empresa só">
            {identidade?.isAdmin === true
              ? 'Para gerenciar outra empresa, vincule-se a ela: em Pessoas, abra o seu cadastro e use "Vincular a uma empresa". Ela passa a aparecer aqui.'
              : 'Para gerenciar outra empresa, peça ao gestor dela para incluir você como colaborador. Ela passa a aparecer aqui.'}
          </Aviso>
        )}
      </div>
    </Modal>
  );
}
